import { useTranslation } from 'react-i18next'
import { VehicleOrderStatus } from '../../../helpers/Orders'
import { hasTimeElapsed, printDate } from '../../../helpers/Utils'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import './OrderDetails.scss'
import { VehicleOrderViewModel } from '@ail/orders-api'

interface IOrderDetailsProps {
  vehicleOrderData?: VehicleOrderViewModel
  isInternalUser: boolean
  altLayout?: boolean
}
enum StepStatus {
  Ok = 'ok',
  Waiting = 'waiting',
  Pending = 'pending',
  Issue = 'issue',
}

function OrderDetails(props: IOrderDetailsProps) {
  const { isInternalUser } = props
  const { t } = useTranslation('common')

  const maxDurationPassed = hasTimeElapsed(props.vehicleOrderData?.earliestDelivery, 45)
  const isExpiring =
    !!props.vehicleOrderData?.isConfirmationExpiring &&
    props.vehicleOrderData.status !== VehicleOrderStatus.Delivered &&
    (isInternalUser || !maxDurationPassed)
  const isUndeliveredInternal = !!props.vehicleOrderData?.isUndelivered && isInternalUser

  const statusValues: Record<VehicleOrderStatus, number> = {
    [VehicleOrderStatus.NewRequest]: 0,
    [VehicleOrderStatus.InternalApproval]: 1,
    [VehicleOrderStatus.OrderInProgress]: 2,
    [VehicleOrderStatus.SupplierConfirmation]: 3,
    [VehicleOrderStatus.PendingProduction]: 4,
    [VehicleOrderStatus.ProductionInProgress]: 5,
    [VehicleOrderStatus.OnStock]: 6,
    [VehicleOrderStatus.DeliveringToLogistic]: 7,
    [VehicleOrderStatus.InPreparation]: 8,
    [VehicleOrderStatus.PendingDeliveryConfirmation]: 9,
    [VehicleOrderStatus.Delivering]: 10,
    [VehicleOrderStatus.Delivered]: 11,
  }
  function printDateIfValid(dateArg: string | Date | undefined, withTime?: boolean) {
    if (!dateArg) return null

    const receivedDate = new Date(dateArg)

    const currentYear = new Date().getFullYear()

    const minValidYear = currentYear - 50
    const maxValidYear = currentYear + 50

    const year = receivedDate.getFullYear()
    if (year < minValidYear || year > maxValidYear) return null

    return printDate(receivedDate, withTime)
  }

  function getStepClass(step: number) {
    return `orderStep ${getStepStatus(step)}`
  }
  function getStepIcon(step: number) {
    const stepStatus = getStepStatus(step)
    switch (stepStatus) {
      case StepStatus.Ok:
        return 'icon-hex-ok'
      case StepStatus.Issue:
        return 'icon-hex-issue'
      case StepStatus.Pending:
        return 'icon-hex-pending'
      default:
        return 'icon-hex-empty'
    }
  }

  function getStepStatus(step: number) {
    if (!props.vehicleOrderData?.status) {
      return StepStatus.Waiting
    }
    if (statusValues[props.vehicleOrderData?.status] > step - 1) {
      return StepStatus.Ok
    }
    if (statusValues[props.vehicleOrderData?.status] === step - 1) {
      return StepStatus.Pending
    }
    if (statusValues[props.vehicleOrderData?.status] < step - 1) {
      return StepStatus.Waiting
    }
    return StepStatus.Issue
  }
  function isStepStatusOk(currentStep: number) {
    if (!props.vehicleOrderData?.status) {
      return false
    }
    return statusValues[props.vehicleOrderData?.status] >= currentStep
  }

  const allSteps = [
    <div className={getStepClass(0)}>
      <strong title={t('NEW_REQUEST')}>{t('NEW_REQUEST')}</strong>
      <i className={getStepIcon(0)} />
      <div className="stepDate">
        <span>{isStepStatusOk(0) ? printDateIfValid(props.vehicleOrderData?.created) : null}</span>
      </div>
    </div>,
    <div className={getStepClass(1)}>
      <strong title={t('INTERNAL_APPROVAL')}>{t('INTERNAL_APPROVAL')}</strong>
      <i className={getStepIcon(1)} />
      <div className="stepDate"></div>
    </div>,
    <div className={getStepClass(2)}>
      <strong title={t('ORDER_IN_PROGRESS')}>{t('ORDER_IN_PROGRESS')}</strong>
      <i className={getStepIcon(2)} />
      <div className="stepDate">
        <span>{isStepStatusOk(2) ? printDateIfValid(props.vehicleOrderData?.orderDate) : null}</span>
      </div>
    </div>,
    <div className={getStepClass(3)}>
      <strong title={t('SUPPLIER_CONFIRMATION')}>{t('SUPPLIER_CONFIRMATION')}</strong>
      <i className={getStepIcon(3)} />
      <div className="stepDate">
        <span>{isStepStatusOk(3) ? printDateIfValid(props.vehicleOrderData?.confirmationDate) : null}</span>
        {isStepStatusOk(3) && isInternalUser && !props.vehicleOrderData?.confirmationDate && (
          <span style={{ color: 'red' }}>Missing supplier confirmation date</span>
        )}
      </div>
    </div>,
    <div className={getStepClass(4)}>
      <strong title={t('PENDING_TO_PRODUCTION')}>{t('PENDING_TO_PRODUCTION')}</strong>
      <i className={getStepIcon(4)} />
      <div className="stepDate">
        <span>{isStepStatusOk(4) && isInternalUser ? props.vehicleOrderData?.supplierNumber : null}</span>

        {isStepStatusOk(4) && isInternalUser && !props.vehicleOrderData?.supplierNumber ? (
          <span style={{ color: 'red' }}>Missing supplier confirmation number</span>
        ) : null}
      </div>
    </div>,
    <div className={getStepClass(5)}>
      <strong title={t('PRODUCTION_IN_PROGRESS')}>{t('PRODUCTION_IN_PROGRESS')}</strong>
      <i className={getStepIcon(5)} />
      <div className="stepDate">
        {isStepStatusOk(5) && isInternalUser ? <span>{props.vehicleOrderData?.chassisNumber} </span> : null}
        {isStepStatusOk(5) && isInternalUser && !props.vehicleOrderData?.chassisNumber ? (
          <span style={{ color: 'red' }}>Missing vehicle chassis number</span>
        ) : null}
      </div>
    </div>,
    <div className={getStepClass(6)}>
      <strong title={t('ON_STOCK')}>{t('ON_STOCK')}</strong>
      <i className={getStepIcon(6)} />
      <div className="stepDate">
        {!isStepStatusOk(5) && isInternalUser && (
          <span style={{ color: 'red' }}>{printDateIfValid(props.vehicleOrderData?.arrivalDate)}</span>
        )}
      </div>
    </div>,
    <div className={getStepClass(7)}>
      <strong title={t('DELIVERING_TO_LOGISTIC')}>{t('DELIVERING_TO_LOGISTIC')}</strong>
      <i className={getStepIcon(7)} />
      <div className="stepDate">
        <span>
          {isStepStatusOk(7) && props.vehicleOrderData?.arrivalDate
            ? printDateIfValid(props.vehicleOrderData?.arrivalDate)
            : null}
        </span>
        {isStepStatusOk(7) &&
          isInternalUser &&
          !props.vehicleOrderData?.estimatedDeliveryDate &&
          !props.vehicleOrderData?.arrivalDate && (
            <span style={{ color: 'red' }}>Missing estimated / arrival delivery date</span>
          )}
        {!isStepStatusOk(7) &&
          isInternalUser &&
          props.vehicleOrderData?.estimatedDeliveryDate &&
          !props.vehicleOrderData?.arrivalDate && (
            <span style={{ color: 'red' }}>{printDateIfValid(props.vehicleOrderData?.estimatedDeliveryDate)}</span>
          )}
      </div>
    </div>,
    <div className={getStepClass(8)}>
      <strong title={t('IN_PREPARATION')}>{t('IN_PREPARATION')}</strong>
      <i className={getStepIcon(8)} />
      <div className="stepDate"></div>
    </div>,
    <div className={isExpiring ? 'orderStep issue' : getStepClass(9)}>
      <strong title={t('PENDING_DELIVERY_CONFIRMATION')}>{t('PENDING_DELIVERY_CONFIRMATION')}</strong>
      <i className={isExpiring ? 'icon-hex-issue' : getStepIcon(9)} />
      <div className="stepDate">
        <span>{isStepStatusOk(9) ? printDateIfValid(props.vehicleOrderData?.earliestDelivery) : null}</span>
        {isStepStatusOk(9) && isInternalUser && !props.vehicleOrderData?.earliestDelivery && (
          <span style={{ color: 'red' }}>Missing earliest delivery date</span>
        )}
      </div>
    </div>,
    <div className={getStepClass(10)}>
      <strong title={t('DELIVERING')}>{t('DELIVERING')}</strong>
      <i className={getStepIcon(10)} />
      <div className="stepDate">
        <span>{isStepStatusOk(10) ? printDateIfValid(props.vehicleOrderData?.confirmedDelivery) : null}</span>
        {isStepStatusOk(10) && isInternalUser && !props.vehicleOrderData?.confirmedDelivery && (
          <span style={{ color: 'red' }}>Missing confirmed delivery date</span>
        )}
      </div>
    </div>,
    <div className={isUndeliveredInternal && !isExpiring ? 'orderStep issue' : getStepClass(11)}>
      <strong title={t('DELIVERED')}>{t('DELIVERED')}</strong>
      <i className={isUndeliveredInternal && !isExpiring ? 'icon-hex-issue' : getStepIcon(11)} />
      <div className="stepDate">
        <span>{isStepStatusOk(11) ? printDateIfValid(props.vehicleOrderData?.deliveryAtCustomerDate) : null}</span>
        {isStepStatusOk(11) && isInternalUser && !props.vehicleOrderData?.deliveryAtCustomerDate && (
          <span style={{ color: 'red' }}>Missing delivered to customer date</span>
        )}
      </div>
    </div>,
  ]
  return !props.vehicleOrderData ? (
    <div className="orderDetailsArea ordersNew">
      <div className="centerLoader">
        <LoadingSpinner />
      </div>
    </div>
  ) : props.altLayout ? (
    <div className="details orderDetailsArea three-rows">
      <div className="orderDetails">
        {allSteps[0]}
        {allSteps[1]}
        {allSteps[2]}
        {allSteps[3]}
      </div>
      <div className="orderDetails">
        {allSteps[4]}
        {allSteps[5]}
        {allSteps[6]}
        {allSteps[7]}
      </div>
      <div className="orderDetails">
        {allSteps[8]}
        {allSteps[9]}
        {allSteps[10]}
        {allSteps[11]}
      </div>
    </div>
  ) : (
    <div className="orderDetailsArea ordersNew">
      <div className="orderDetails">
        {allSteps[0]}
        {allSteps[1]}
        {allSteps[2]}
        {allSteps[3]}
        {allSteps[4]}
        {allSteps[5]}
      </div>
      <div className="orderDetails">
        {allSteps[6]}
        {allSteps[7]}
        {allSteps[8]}
        {allSteps[9]}
        {allSteps[10]}
        {allSteps[11]}
      </div>
    </div>
  )
}

export default OrderDetails
